import { create } from 'zustand';
import apiClient from '../lib/api';

export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskStatus = 'backlog' | 'todo' | 'in-progress' | 'review' | 'done';

export interface Task {
  id: string;
  projectId: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  assigneeId?: string;
  assigneeName?: string;
  assigneeAvatar?: string;
  dueDate?: string;
  tags: string[];
  storyPoints?: number;
  timeSpent?: number;
  attachmentsCount: number;
  commentsCount: number;
  subtasks: {
    total: number;
    completed: number;
  };
  createdAt: string;
  updatedAt: string;
  code: string;
}

function normalizeTags(raw: any): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter((t) => typeof t === 'string');
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter((t) => typeof t === 'string');
    } catch {
      // ignore
    }
  }
  return [];
}

function normalizeTask(raw: any): Task {
  const status = (raw?.status ?? 'todo') as TaskStatus;
  const priority = (raw?.priority ?? 'medium') as TaskPriority;

  const id = raw?.id ?? '';
  const computedCode =
    typeof id === 'string' && id.length > 0 ? `TSK-${id.replace(/-/g, '').slice(0, 8).toUpperCase()}` : '';
  const code = raw?.code ?? raw?.task_code ?? raw?.task_number ?? computedCode;

  return {
    id,
    projectId: raw?.project_id ?? raw?.projectId ?? '',
    title: raw?.title ?? '',
    description: raw?.description ?? undefined,
    status,
    priority,
    assigneeId: raw?.assignee_id ?? raw?.assigneeId ?? undefined,
    assigneeName: raw?.assignee_name ?? raw?.assigneeName ?? undefined,
    assigneeAvatar: raw?.assignee_avatar ?? raw?.assigneeAvatar ?? undefined,
    dueDate: raw?.due_date ?? raw?.dueDate ?? undefined,
    tags: normalizeTags(raw?.tags ?? raw?.tag_list),
    storyPoints: raw?.story_points ?? raw?.storyPoints ?? undefined,
    timeSpent: raw?.time_spent ?? raw?.timeSpent ?? undefined,
    attachmentsCount: raw?.attachments_count ?? raw?.attachmentsCount ?? 0,
    commentsCount: raw?.comments_count ?? raw?.commentsCount ?? 0,
    subtasks: raw?.subtasks ?? { total: 0, completed: 0 },
    createdAt: raw?.created_at ?? raw?.createdAt ?? new Date().toISOString(),
    updatedAt: raw?.updated_at ?? raw?.updatedAt ?? new Date().toISOString(),
    code,
  };
}

export interface TaskComment {
  id: string;
  task_id: string;
  author_id: string;
  content: string;
  created_at: string;
}

export interface TaskActivity {
  id: string;
  task_id: string;
  actor_id?: string;
  action: string;
  field?: string;
  from_value?: string;
  to_value?: string;
  created_at: string;
}

export interface TaskAttachment {
  id: string;
  task_id: string;
  file_name: string;
  file_url: string;
  uploaded_by: string;
  created_at: string;
}

interface TaskState {
  tasks: Task[];
  currentTask: Task | null;
  comments: TaskComment[];
  activity: TaskActivity[];
  attachments: TaskAttachment[];
  isLoading: boolean;
  error: string | null;

  fetchTasks: (projectId?: string) => Promise<void>;
  fetchTask: (taskId: string) => Promise<void>;
  updateTaskStatus: (taskId: string, status: TaskStatus) => Promise<void>;
  updateTask: (taskId: string, data: Partial<Task>) => Promise<void>;
  createTask: (data: Partial<Task>) => Promise<void>;
  deleteTask: (taskId: string) => Promise<void>;

  fetchComments: (taskId: string) => Promise<void>;
  addComment: (taskId: string, content: string) => Promise<void>;
  deleteComment: (taskId: string, commentId: string) => Promise<void>;
  fetchActivity: (taskId: string) => Promise<void>;
  fetchAttachments: (taskId: string) => Promise<void>;
  addAttachment: (taskId: string, data: { fileName: string; fileUrl: string }) => Promise<void>;
  deleteAttachment: (taskId: string, attachmentId: string) => Promise<void>;
}

export const useTaskStore = create<TaskState>((set, get) => ({
  tasks: [],
  currentTask: null,
  comments: [],
  activity: [],
  attachments: [],
  isLoading: false,
  error: null,

  fetchTasks: async (projectId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get(`/work/tasks`, { params: { projectId } });
      const { data } = response.data || {};
      set({ tasks: (data || []).map(normalizeTask), isLoading: false });
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        set({ tasks: [], isLoading: false, error: 'Please login to load tasks.' });
        return;
      }

      set({ tasks: [], isLoading: false, error: null });
    }
  },

  fetchTask: async (taskId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiClient.get(`/work/tasks/${taskId}`);
      set({ currentTask: normalizeTask(response.data), isLoading: false });
    } catch (error) {
      const sample = get().tasks.find(t => t.id === taskId) || null;
      set({ currentTask: sample, isLoading: false });
    }
  },

  updateTaskStatus: async (taskId: string, status: TaskStatus) => {
    set(state => ({
      tasks: state.tasks.map(t => t.id === taskId ? { ...t, status } : t),
      currentTask: state.currentTask?.id === taskId ? { ...state.currentTask, status } : state.currentTask
    }));
    try {
      const response = await apiClient.patch(`/work/tasks/${taskId}`, { status });
      const updated = normalizeTask(response.data);
      set((state) => ({
        tasks: state.tasks.map((t) => (t.id === taskId ? updated : t)),
        currentTask: state.currentTask?.id === taskId ? updated : state.currentTask,
      }));
    } catch (e) { console.error(e); }
  },

  updateTask: async (taskId: string, data: Partial<Task>) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.patch(`/work/tasks/${taskId}`, data);
      const updated = normalizeTask(response.data);
      set(state => ({
        currentTask: updated,
        tasks: state.tasks.map(t => t.id === taskId ? updated : t),
        isLoading: false
      }));
    } catch (error) {
      set(state => ({
        currentTask: state.currentTask ? { ...state.currentTask, ...data } : null,
        tasks: state.tasks.map(t => t.id === taskId ? { ...t, ...data } : t),
        isLoading: false
      }));
      throw error;
    }
  },

  createTask: async (data: Partial<Task>) => {
    set({ isLoading: true });
    try {
      const response = await apiClient.post('/work/tasks', data);
      set(state => ({
        tasks: [...state.tasks, normalizeTask(response.data)],
        isLoading: false
      }));
    } catch (error) {
      const newTask = {
        id: Math.random().toString(36).substr(2, 9),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        attachmentsCount: 0,
        commentsCount: 0,
        subtasks: { total: 0, completed: 0 },
        tags: [],
        ...data
      } as Task;

      set(state => ({
        tasks: [...state.tasks, newTask],
        isLoading: false
      }));
      throw error;
    }
  },

  deleteTask: async (taskId: string) => {
    set({ isLoading: true });
    try {
      await apiClient.delete(`/work/tasks/${taskId}`);
      set(state => ({
        tasks: state.tasks.filter(t => t.id !== taskId),
        currentTask: state.currentTask?.id === taskId ? null : state.currentTask,
        isLoading: false
      }));
    } catch (error) {
      set(state => ({
        tasks: state.tasks.filter(t => t.id !== taskId), // Optimistic delete even if API fails in this context?
        currentTask: state.currentTask?.id === taskId ? null : state.currentTask,
        isLoading: false
      }));
      throw error;
    }
  },

  fetchComments: async (taskId: string) => {
    try {
      const res = await apiClient.get(`/work/tasks/${taskId}/comments`);
      set({ comments: res.data?.data ?? [] });
    } catch {
      set({ comments: [] });
    }
  },

  addComment: async (taskId: string, content: string) => {
    await apiClient.post(`/work/tasks/${taskId}/comments`, { content });
    await get().fetchComments(taskId);
    await get().fetchActivity(taskId);
  },

  deleteComment: async (taskId: string, commentId: string) => {
    await apiClient.delete(`/work/tasks/${taskId}/comments/${commentId}`);
    set((state) => ({ comments: state.comments.filter((c) => c.id !== commentId) }));
  },

  fetchActivity: async (taskId: string) => {
    try {
      const res = await apiClient.get(`/work/tasks/${taskId}/activity`);
      set({ activity: res.data?.data ?? [] });
    } catch {
      set({ activity: [] });
    }
  },

  fetchAttachments: async (taskId: string) => {
    try {
      const res = await apiClient.get(`/work/tasks/${taskId}/attachments`);
      set({ attachments: res.data?.data ?? [] });
    } catch {
      set({ attachments: [] });
    }
  },

  addAttachment: async (taskId: string, data: { fileName: string; fileUrl: string }) => {
    await apiClient.post(`/work/tasks/${taskId}/attachments`, data);
    await get().fetchAttachments(taskId);
    await get().fetchActivity(taskId);
  },

  deleteAttachment: async (taskId: string, attachmentId: string) => {
    await apiClient.delete(`/work/tasks/${taskId}/attachments/${attachmentId}`);
    set((state) => ({ attachments: state.attachments.filter((a) => a.id !== attachmentId) }));
  },
}));
